import FingerprintJS, {Agent} from '@fingerprintjs/fingerprintjs';
import {UAParser} from 'ua-parser-js';
import CryptoJS from 'crypto-js';

const JWT_CLICK_SECRET = process.env.REACT_APP_JWT_CLICK_SECRET;

let fpPromise: Promise<Agent> | null = null;

const getFP = () => {
    if (!fpPromise) fpPromise = FingerprintJS.load();
    return fpPromise;
};

const encryptFingerprintData = (fingerprintData: object) => {
    const jsonString = JSON.stringify(fingerprintData);
    return CryptoJS.AES.encrypt(jsonString, CryptoJS.enc.Base64.parse(JWT_CLICK_SECRET ?? ''), {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
    }).toString();
};

const baseFonts = ['monospace', 'sans-serif', 'serif'];
const testFonts = [
    'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Comic Sans MS',
    'Georgia', 'Trebuchet MS', 'Impact', 'Century Gothic', 'Palatino'
];

// Utility function to fetch user fingerprint data
export const getFingerprintData = async (category: string, captcha: object) => {
    try {
        // Load FingerprintJS
        const fp = await getFP();
        const result = await fp.get();

        // Get browser & device details using UAParser
        const parser = new UAParser();
        const deviceInfo = parser.getResult();

        // Capture browser-related information
        const browserData = {
            userAgent: navigator.userAgent,
            browser: deviceInfo.browser.name || 'unknown',
            browserVersion: deviceInfo.browser.version || 'unknown',
            os: deviceInfo.os.name || 'unknown',
            osVersion: deviceInfo.os.version || 'unknown',
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            devicePixelRatio: window.devicePixelRatio || 1,
            browserPlugins: Array.from(navigator.plugins).map(plugin => plugin.name),
            deviceType: deviceInfo.device.type || 'desktop',
            deviceModel: deviceInfo.device.model || 'unknown',
            deviceVendor: deviceInfo.device.vendor || 'unknown',
            language: navigator.language || 'unknown',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
            cookiesEnabled: navigator.cookieEnabled,
        };

        // Capture network details (IP must be fetched from backend)
        const connection = (navigator as any).connection || {}; // Avoid TS errors for non-standard APIs
        const networkData = {
            ipAddress: null, // Needs to be retrieved via API
            connectionType: connection.effectiveType || 'unknown',
            downlinkSpeed: connection.downlink || null, // Mbps
        };

        const [batteryStatus, installedFonts, gpuInfo] = await Promise.all([
            getBatteryLevel(),
            detectFonts(),
            getGPUInfo(),
        ]);

        // Capture hardware-related data
        const hardwareData = {
            cpuCores: navigator.hardwareConcurrency || null,
            deviceMemory: (navigator as any).deviceMemory || null, // GB
            maxTouchPoints: navigator.maxTouchPoints || 0,
            gpuInfo,
            batteryStatus,
            installedFonts
        };

        const behavioralData: any = {sessionId: generateSessionId(result.visitorId)};

        const storageData: any = {
            localStorageKeys: Object.keys(localStorage),
            previousVisits: document.cookie.includes('visited=true') ? 'Yes' : 'No'
        };
        document.cookie = 'visited=true; path=/;'; // Set cookie for future visits

        if (category === 'clickValidation') { // Extra data for click validation case
            document.addEventListener('mousemove', onMove);
            document.addEventListener('click', onClick);

            behavioralData.mouseMovements = mouseMovements;
            behavioralData.clickPatterns = clickPatterns;
            behavioralData.captcha = captcha || null;
            storageData.canvasFingerprint = getCanvasFingerprint();
        }

        // Full fingerprint data
        const fingerprintData = {
            fingerprintID: result.visitorId,
            browserData,
            networkData,
            hardwareData,
            behavioralData,
            storageData,
            timestamp: new Date().toISOString(),
        };

        if (category === 'clickValidation') {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('click', onClick);
        }

        return encryptFingerprintData(fingerprintData);
    } catch (error) {
        console.error('Error collecting fingerprint data:', error);
        return null;
    }
};

const mouseMovements: { x: number; y: number }[] = [];
const clickPatterns: { x: number; y: number }[] = [];
const MAX_POINTS = 50;

function onMove(event: MouseEvent) {
    mouseMovements.push({x: event.clientX, y: event.clientY});
    if (mouseMovements.length > MAX_POINTS) mouseMovements.shift();
}

function onClick(event: MouseEvent) {
    clickPatterns.push({x: event.clientX, y: event.clientY});
}

const getCanvasFingerprint = (): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return 'Not supported';

    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Fingerprint', 2, 2);

    return canvas.toDataURL();
};

const getGPUInfo = (): string => {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') as WebGLRenderingContext
            || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
        if (!gl) return 'Not supported';
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        return debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string : 'unknown GPU';
    } catch (e) {
        console.error('Error retrieving GPU info:', e);
        return 'Error retrieving GPU info';
    }
};

const getBatteryLevel = async (): Promise<string> => {
    try {
        if ('getBattery' in navigator) {
            const battery = await (navigator as any).getBattery();
            return `${Math.round(battery.level * 100)}%`;
        }
        return 'Not supported';
    } catch {
        return 'unknown';
    }
};

const generateSessionId = (fingerprintID: string): string => {
    const sessionKey = `session_${fingerprintID}`;
    const storedSession = localStorage.getItem(sessionKey);

    if (storedSession) {
        const {sessionId, timestamp} = JSON.parse(storedSession);
        const elapsedTime = Date.now() - timestamp;
        if (elapsedTime < 30 * 60 * 1000) return sessionId;
    }

    const newSessionId = `${fingerprintID}-${window.crypto.randomUUID()}`;
    localStorage.setItem(sessionKey, JSON.stringify({sessionId: newSessionId, timestamp: Date.now()}));
    return newSessionId;
};

const detectFonts = (): string[] => {
    const detectedFonts: string[] = [];
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return detectedFonts; // Return empty array if Canvas API is not available

    // Generate a reference size for each base font
    const referenceSizes: Record<string, number> = {};
    baseFonts.forEach((baseFont) => {
        context.font = `72px ${baseFont}`;
        referenceSizes[baseFont] = context.measureText('A').width;
    });

    // Check each test font
    testFonts.forEach((font) => {
        let isFontAvailable = false;
        baseFonts.forEach((baseFont) => {
            context.font = `72px ${font}, ${baseFont}`;
            const measuredWidth = context.measureText('A').width;
            if (measuredWidth !== referenceSizes[baseFont]) isFontAvailable = true;
        });
        if (isFontAvailable) detectedFonts.push(font);
    });

    return detectedFonts;
};

export default getFingerprintData;